import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES, scrollToId } from "@/constants/site";
import { useSiteImage } from "@/context/ImagesContext";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const heroSrc = useSiteImage("hero", IMAGES.hero.url);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} id="hero" data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: imgY }}>
        <motion.img
          src={heroSrc}
          alt={IMAGES.hero.alt}
          className="h-[115%] w-full object-cover"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-espresso/70 via-forest/45 to-espresso/85" />
      <div className="absolute inset-0 bg-gradient-to-r from-espresso/40 to-transparent" />
      <motion.div
        className="absolute inset-0 bg-espresso"
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 1.4, delay: 0.15, ease: "easeOut" }}
      />

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center"
      >
        <MaskedLine delay={0.55} className="eyebrow !text-gold mb-6">
          Sakleshpur · Karnataka · Est. 1844
        </MaskedLine>
        <h1 data-testid="hero-heading" className="font-display font-light text-cream leading-[0.95] tracking-tight">
          <MaskedLine delay={0.75} className="text-6xl sm:text-7xl lg:text-8xl">
            Masthare
          </MaskedLine>
          <MaskedLine delay={0.92} className="text-6xl sm:text-7xl lg:text-8xl italic text-gold">
            Estate
          </MaskedLine>
        </h1>
        <MaskedLine
          delay={1.15}
          className="mt-7 font-display text-xl sm:text-2xl lg:text-3xl text-cream/85 font-light max-w-xl"
        >
          180 Years of Legacy in the Western Ghats
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.5, ease: EASE }}
          className="mt-10"
        >
          <button data-testid="hero-cta-button" className="btn-gold" onClick={() => scrollToId("contact")}>
            Get in Touch
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("story")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-cream/60 hover:text-gold transition-colors"
        aria-label="Scroll to Our Story"
      >
        <span className="font-mono-alt text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <ArrowDown size={16} className="scroll-hint-dot" />
      </motion.button>
    </section>
  );
};

export default Hero;
